/**
 * One question: the stem, and the input for its type.
 *
 * MCQ → radio (one choice), MSQ → checkbox (many), NAT → a numeric field.
 *
 * The NAT field keeps the student's raw text in local state and only commits a
 * parsed number. Without that, typing "-" or "3." — both legitimate midpoints
 * of typing a real number — would either save garbage or fight the cursor.
 */
import { useEffect, useRef, useState } from "react";
import type {
  AnswerResponse,
  AttemptPaperItem,
  QuestionOption,
} from "@exam/shared";
import { Badge } from "@/components/ui";
import { cn } from "@/lib/cn";
import { MarkdownInline, QuestionBody } from "@/lib/markdown";
import { CodeAnswer } from "./CodeAnswer";
import {
  natValueToText,
  parseNatValue,
  toggleMsqKey,
} from "@/lib/exam/answerState";

const TYPE_HINT: Record<string, string> = {
  MCQ: "Choose one option.",
  MSQ: "Choose all options that apply.",
  NAT: "Enter a number.",
  CODE: "Write the code, then run the tests.",
};

export function QuestionView({
  item,
  number,
  total,
  response,
  onChange,
}: {
  item: AttemptPaperItem;
  /** 1-based. */
  number: number;
  total: number;
  response: AnswerResponse | null;
  onChange: (response: AnswerResponse | null) => void;
}) {
  const { question, marks, negative_marks } = item;

  return (
    <article className="flex flex-col">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <h1 className="text-sm font-semibold text-ink">
          Question <span className="tabular">{number}</span>
          <span className="font-normal text-ink-faint">
            {" "}
            of <span className="tabular">{total}</span>
          </span>
        </h1>

        <Badge tone="neutral">{question.type}</Badge>

        <span className="text-2xs text-ink-muted">
          <span className="tabular">+{marks}</span>
          {negative_marks > 0 ? (
            <>
              {" / "}
              <span className="tabular text-violation">-{negative_marks}</span>
            </>
          ) : null}
        </span>

        <span className="ml-auto text-2xs text-ink-faint">
          {TYPE_HINT[question.type]}
        </span>
      </header>

      <div className="px-4 py-4">
        <QuestionBody source={question.body_md} />

        {question.figure_url ? (
          <figure className="mt-3">
            <img
              src={question.figure_url}
              alt={`Figure for question ${number}`}
              // Diagrams are the question. Cap the height so a tall figure does
              // not push the options below the fold, but never scale up past
              // natural size and blur the labels.
              className="max-h-96 max-w-full rounded-md border border-border bg-surface"
            />
          </figure>
        ) : null}

        <div className="mt-4">
          {question.type === "CODE" ? (
            <CodeAnswer
              questionId={question.id}
              starterCode={question.starter_code ?? null}
              tests={item.tests ?? []}
              value={
                response?.type === "CODE"
                  ? {
                      source: response.source,
                      passed: response.passed,
                      total: response.total,
                      results: response.results,
                    }
                  : null
              }
              onChange={(next) =>
                onChange({
                  type: "CODE",
                  source: next.source,
                  passed: next.passed,
                  total: next.total,
                  results: next.results,
                })
              }
            />
          ) : question.type === "NAT" ? (
            // Keyed on the question so navigating away and back rebuilds the
            // field from the saved value instead of carrying stale text over.
            <NatInput
              key={question.id}
              response={response}
              onChange={onChange}
            />
          ) : (
            <OptionList
              questionId={question.id}
              type={question.type}
              options={question.options}
              response={response}
              onChange={onChange}
            />
          )}
        </div>
      </div>
    </article>
  );
}

function OptionList({
  questionId,
  type,
  options,
  response,
  onChange,
}: {
  questionId: string;
  type: "MCQ" | "MSQ";
  options: QuestionOption[];
  response: AnswerResponse | null;
  onChange: (response: AnswerResponse | null) => void;
}) {
  const optionOrder = options.map((o) => o.key);

  const isSelected = (key: string): boolean => {
    if (type === "MCQ") return response?.type === "MCQ" && response.key === key;
    return response?.type === "MSQ" && response.keys.includes(key);
  };

  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="sr-only">Options</legend>

      {options.map((option) => {
        const selected = isSelected(option.key);
        const inputId = `${questionId}-${option.key}`;

        return (
          <label
            key={option.key}
            htmlFor={inputId}
            className={cn(
              "flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2",
              "transition-colors",
              selected
                ? "border-accent bg-accent-soft"
                : "border-border bg-surface hover:bg-surface-sunken",
            )}
          >
            <input
              id={inputId}
              type={type === "MCQ" ? "radio" : "checkbox"}
              name={questionId}
              checked={selected}
              onChange={() => {
                if (type === "MCQ") {
                  onChange({ type: "MCQ", key: option.key });
                } else {
                  onChange(toggleMsqKey(response, option.key, optionOrder));
                }
              }}
              className="mt-0.5 size-3.5 shrink-0 accent-accent"
            />
            <span className="text-2xs font-medium text-ink-faint uppercase">
              {option.key}
            </span>
            <MarkdownInline source={option.text} className="min-w-0" />
          </label>
        );
      })}
    </fieldset>
  );
}

function NatInput({
  response,
  onChange,
}: {
  response: AnswerResponse | null;
  onChange: (response: AnswerResponse | null) => void;
}) {
  const [text, setText] = useState(() => natValueToText(response));
  const [invalid, setInvalid] = useState(false);
  /** The last text this field itself produced, to tell own edits from polls. */
  const selfEditedRef = useRef(natValueToText(response));

  // Adopt a server value only when it differs from what this field last
  // emitted — i.e. it came from a poll or an earlier session, not from typing.
  // Without this check every keystroke would be overwritten by the round trip.
  const canonical = natValueToText(response);
  useEffect(() => {
    if (canonical === selfEditedRef.current) return;
    selfEditedRef.current = canonical;
    setText(canonical);
    setInvalid(false);
  }, [canonical]);

  return (
    <div className="flex max-w-xs flex-col gap-1">
      <label
        htmlFor="nat-answer"
        className="text-xs font-medium text-ink-muted"
      >
        Your answer
      </label>
      <input
        id="nat-answer"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          selfEditedRef.current = raw;

          const parsed = parseNatValue(raw);
          if (parsed === undefined) {
            // Not a number yet ("-", "3."). Keep the text, save nothing.
            setInvalid(raw.trim() !== "");
            return;
          }
          setInvalid(false);
          onChange(parsed === null ? null : { type: "NAT", value: parsed });
        }}
        placeholder="e.g. 3.14"
        className={cn(
          "tabular h-8 w-full rounded-md border bg-surface px-2.5 text-sm text-ink",
          "transition-colors placeholder:text-ink-faint",
          invalid ? "border-violation" : "border-border-strong",
        )}
      />
      <p
        className={cn(
          "text-2xs",
          invalid ? "text-violation" : "text-ink-faint",
        )}
      >
        {invalid
          ? "Enter a number, for example 3.14 or -2."
          : "Decimals and negative numbers are allowed."}
      </p>
    </div>
  );
}
